import { MapUtils } from "../../../../core/MapUtils";
import { ShaderName } from "./ShaderName";
import { NetworkChildNodeType } from "../../../poly/NodeContext";
import { ArrayUtils } from "../../../../core/ArrayUtils";
export class TypedNodeTraverser {
  constructor(_parent_node, _shader_names, _input_names_for_shader_name_method, _options) {
    this._parent_node = _parent_node;
    this._shader_names = _shader_names;
    this._input_names_for_shader_name_method = _input_names_for_shader_name_method;
    this._options = _options;
    this._leaves_graph_id = /* @__PURE__ */ new Map();
    this._graph_ids_by_shader_name = /* @__PURE__ */ new Map();
    this._outputs_by_graph_id = /* @__PURE__ */ new Map();
    this._depth_by_graph_id = /* @__PURE__ */ new Map();
    this._graph_id_by_depth = /* @__PURE__ */ new Map();
    this._graph = this._parent_node.scene().graph;
  }
  _traverseChildren() {
    var _a;
    return ((_a = this._options) == null ? void 0 : _a.traverseChildren) || false;
  }
  reset() {
    this._leaves_graph_id.clear();
    this._graph_ids_by_shader_name.clear();
    this._outputs_by_graph_id.clear();
    this._depth_by_graph_id.clear();
    this._graph_id_by_depth.clear();
    this._shader_names.forEach((shader_name) => {
      this._graph_ids_by_shader_name.set(shader_name, /* @__PURE__ */ new Map());
    });
  }
  shaderNames() {
    return this._shader_names;
  }
  input_names_for_shader_name(root_node, shader_name) {
    return this._input_names_for_shader_name_method(root_node, shader_name);
  }
  traverse(rootNodes) {
    this.reset();
    for (let shader_name of this.shaderNames()) {
      this._leaves_graph_id.set(shader_name, /* @__PURE__ */ new Map());
    }
    for (let shader_name of this.shaderNames()) {
      this._shader_name = shader_name;
      for (let rootNode of rootNodes) {
        this._findLeavesFromRootNode(rootNode);
        this._setNodesDepth();
      }
    }
    this._depth_by_graph_id.forEach((depth, graph_id) => {
      if (depth != null) {
        MapUtils.pushOnArrayAtEntry(this._graph_id_by_depth, depth, graph_id);
      }
    });
  }
  leavesFromNodes(nodes) {
    var _a;
    this._shader_name = ShaderName.LEAVES_FROM_NODES_SHADER;
    this._graph_ids_by_shader_name.set(this._shader_name, /* @__PURE__ */ new Map());
    this._leaves_graph_id.set(this._shader_name, /* @__PURE__ */ new Map());
    for (let node of nodes) {
      this._findLeaves(node);
    }
    const node_ids = [];
    (_a = this._leaves_graph_id.get(this._shader_name)) == null ? void 0 : _a.forEach((value, key) => {
      node_ids.push(key);
    });
    return this._graph.nodesFromIds(node_ids);
  }
  nodesForShaderName(shader_name) {
    const depths = [];
    this._graph_id_by_depth.forEach((value, key) => {
      depths.push(key);
    });
    depths.sort((a, b) => a - b);
    const nodes = [];
    const node_id_used_state = /* @__PURE__ */ new Map();
    depths.forEach((depth) => {
      const graph_ids_for_depth = this._graph_id_by_depth.get(depth);
      if (graph_ids_for_depth) {
        graph_ids_for_depth.forEach((graph_id) => {
          var _a;
          const is_present = (_a = this._graph_ids_by_shader_name.get(shader_name)) == null ? void 0 : _a.get(graph_id);
          if (is_present) {
            const node = this._graph.nodeFromId(graph_id);
            this._addNodesWithChildren(node, node_id_used_state, nodes, shader_name);
          }
        });
      }
    });
    return nodes;
  }
  sortedNodes() {
    const depths = [];
    this._graph_id_by_depth.forEach((ids, depth) => {
      depths.push(depth);
    });
    depths.sort((a, b) => a - b);
    const nodes = [];
    const node_id_used_state = /* @__PURE__ */ new Map();
    depths.forEach((depth) => {
      const graph_ids_for_depth = this._graph_id_by_depth.get(depth);
      if (graph_ids_for_depth) {
        for (let graph_id of graph_ids_for_depth) {
          const node = this._graph.nodeFromId(graph_id);
          if (node) {
            this._addNodesWithChildren(node, node_id_used_state, nodes);
          }
        }
      }
    });
    return nodes;
  }
  _addNodesWithChildren(node, node_id_used_state, accumulated_nodes, shader_name) {
    if (!node_id_used_state.get(node.graphNodeId())) {
      accumulated_nodes.push(node);
      node_id_used_state.set(node.graphNodeId(), true);
    }
  }
  _findLeavesFromRootNode(root_node) {
    var _a;
    (_a = this._graph_ids_by_shader_name.get(this._shader_name)) == null ? void 0 : _a.set(root_node.graphNodeId(), true);
    const input_names = this.input_names_for_shader_name(root_node, this._shader_name);
    if (input_names) {
      for (let input_name of input_names) {
        const input = root_node.io.inputs.named_input(input_name);
        if (input) {
          MapUtils.pushOnArrayAtEntry(this._outputs_by_graph_id, input.graphNodeId(), root_node.graphNodeId());
          this._findLeaves(input);
        }
      }
    }
    this._outputs_by_graph_id.forEach((outputs, graph_id) => {
      this._outputs_by_graph_id.set(graph_id, ArrayUtils.uniq(outputs));
    });
  }
  _findLeaves(node) {
    var _a;
    (_a = this._graph_ids_by_shader_name.get(this._shader_name)) == null ? void 0 : _a.set(node.graphNodeId(), true);
    const inputs = this._findInputs(node);
    const compact_inputs = ArrayUtils.compact(inputs);
    const input_graph_ids = ArrayUtils.uniq(compact_inputs.map((n) => n.graphNodeId()));
    const unique_inputs = input_graph_ids.map((graph_id) => this._graph.nodeFromId(graph_id));
    if (unique_inputs.length > 0) {
      for (let input of unique_inputs) {
        MapUtils.pushOnArrayAtEntry(this._outputs_by_graph_id, input.graphNodeId(), node.graphNodeId());
        this._findLeaves(input);
      }
    } else {
      this._leaves_graph_id.get(this._shader_name).set(node.graphNodeId(), true);
    }
  }
  _findInputs(node) {
    var _a, _b;
    if (this._traverseChildren()) {
      if (node.type() == NetworkChildNodeType.INPUT) {
        return ((_a = node.parent()) == null ? void 0 : _a.io.inputs.inputs()) || [];
      } else {
        if (node.childrenAllowed()) {
          const output_node = (_b = node.childrenController) == null ? void 0 : _b.outputNode();
          return [output_node];
        } else {
          return node.io.inputs.inputs();
        }
      }
    } else {
      return node.io.inputs.inputs();
    }
  }
  _setNodesDepth() {
    this._leaves_graph_id.forEach((booleans_by_graph_id, shader_name) => {
      booleans_by_graph_id.forEach((boolean, graph_id) => {
        this._setNodeDepth(graph_id);
      });
    });
  }
  _setNodeDepth(graph_id, depth = 0) {
    const current_depth = this._depth_by_graph_id.get(graph_id);
    if (current_depth != null) {
      this._depth_by_graph_id.set(graph_id, Math.max(current_depth, depth));
    } else {
      this._depth_by_graph_id.set(graph_id, depth);
    }
    const output_ids = this._outputs_by_graph_id.get(graph_id);
    if (output_ids) {
      output_ids.forEach((output_id) => {
        this._setNodeDepth(output_id, depth + 1);
      });
    }
  }
}
